import { SplitFactory } from '@splitsoftware/splitio'
import SplitIO from '@splitsoftware/splitio/types/splitio'
import path from 'path'

import logger from 'shared/services/logger'
import settings, { APP_ENV } from 'shared/common/settings'

const serverKey = settings.getValue('SSR_SPLIT_IO_AUTHORIZATION_KEY')
const appEnv = settings.appEnv || 'development'

const serverAuthorization = new Map<APP_ENV, string>([
  ['production', serverKey],
  ['staging', serverKey],
  ['development', 'localhost'],
  ['preview', serverKey],
  ['local', 'localhost'],
])

const serverFeatures = new Map<APP_ENV, string | undefined>([
  [
    'development',
    path.resolve(
      process.cwd(),
      '../shared/services/feature-flags',
      'splitio.ssr.local.yaml',
    ),
  ],
  [
    'local',
    path.resolve(
      process.cwd(),
      '../shared/services/feature-flags',
      'splitio.ssr.local.yaml',
    ),
  ],
])

export const serverFactory: SplitIO.ISDK = SplitFactory({
  core: { authorizationKey: serverAuthorization.get(appEnv) || 'localhost' },
  features: serverFeatures.get(appEnv),
  debug: false,
})

export class ServiceFeatureFlagServerSide {
  constructor(private client: SplitIO.IClient) {}

  async getTreatment(key: string, splitName: string): Promise<boolean> {
    try {
      await this.client.ready()

      const treatment = this.client.getTreatment(key, splitName)

      return treatment === 'on'
    } catch (err) {
      logger.debug(`error loading server treatment: ${err}`)
      return false
    }
  }

  async getTreatments(
    key: string,
    splits: string[],
  ): Promise<SplitIO.Treatments> {
    try {
      await this.client.ready()

      return this.client.getTreatments(key, splits)
    } catch (err) {
      logger.debug(`error loading server treatments: ${err}`)
      return {}
    }
  }
}

export const client = new ServiceFeatureFlagServerSide(serverFactory.client())

export enum Treatments {
  dispatching = 'Dispatching',
  customEmails = 'URSA-1433_Custom_Email_Templates',
  programManagementV2 = 'GCYC_669_program_management_v2',
  templateLibrary = 'Template_and_Milestone_Library',
  workatoEmbed = 'labs_626_workato_full_embed',
  workspaces = 'ENG-1755_Workspaces',
  messaging = 'ENG-3634_Messaging_Threads',
  taskMessaging = 'ENG-3634_Messaging_Threads_Tasks',
  graphQLVersion = 'ENG-4852_GraphQL_V2',
  unifiedMergeTags = 'ENG-5851_Unified_Merge_Tags',
  planViewMvp = 'ENG-4267_Plan_View_MVP',
  theme = 'ENG_7043_Theme',
  messagingNotesMigration = 'ENG-3634_Messaging_Notes_Migration',
  timeTracking = 'ENG-8240_K2_Time_Tracking',
  customFields = 'k2-custom-fields',
  coreWriteback = 'summit-core-writeback',
  ganttV2 = 'Gantt_v2',
  winTheUserDashboard = 'Win-The-User-Dashboard',
  reportsV2 = 'Reports_v2',
  boardViewConfiguration = 'Board_View_Configuration',
  integrationMarketplaceV2 = 'Integration_Marketplace_V2',
  customerRolesViewPermission = 'Customer_Roles_View_Permission',
  bulkEdit = 'Bulk_Edit_Plan_View',
  participantTasks = 'Participant_Tasks',
  reportAIAssistant = 'Report_AI_Assistant',
  reportingV2 = 'Reporting_V2',
  resourceAvailability = 'Resource_Availability',
  userAttributes = 'User_Attributes',
  projectHistory = 'project_history',
  disableLegacyAccess = 'Disable_Legacy_Access',
  legacyReportsAvailable = 'Legacy_Reports_Available',
  workspaceScheduleSettings = 'Workspace_Schedule_Settings',
  paginatedPotentialAssignees = 'Paginated_Potential_Assignees',
  compassAndPortalResources = 'ENG-5830_Compass_Resources',
  resourcePlanningPage = 'Resource_Planning_Page',
  peopleTimelinePage = 'People_Timeline_Page',
  requireTimeTracking = 'Require_Time_Tracking',
  templateLibraryMoveUnit = 'template_library_move_unit',
  simulateTemplateForecast = 'Simulate_Template_Forecast',
  recommendTeamForSimulatedProject = 'Recommend_Team_For_Simulated_Project',
  estimatedCapacityPageV2 = 'Estimated_Capacity_Page_v2',
  timeTrackingInteractiveReporting = 'Time_Tracking_Interactive_Reporting',
  brandEnabled = 'Brands_v2',
  globalTasksOnlyUnblockedTasks = 'Global_Tasks_Only_Unblocked_Tasks',
  migrateProjectsToV2 = 'migrate_projects_to_v2',
  debugProjectForecasting = 'Debug_Project_Forecasting',
  dataExports = 'Data_Exports',
  boardViewTemplateSupport = 'Board_View_Template_Support',
}

export const isTreatmentEnabled = (
  treatments: SplitIO.Treatments,
  treatment: Treatments,
): boolean => {
  if (!treatments) return false

  const ff = Object.keys(treatments).find((key) => key === treatment)

  if (!ff) return false

  return treatments[ff] === 'on'
}

export const formatTreatments = (
  treatments?: SplitIO.Treatments,
): Record<Treatments, boolean> => {
  const flags: Partial<Record<Treatments, boolean>> = {}
  if (!treatments) return flags as Record<Treatments, boolean>

  Object.keys(Treatments).forEach((treatmentKey) => {
    const treatment = Treatments[treatmentKey as keyof typeof Treatments]
    flags[treatment] = isTreatmentEnabled(treatments, treatment)
  })

  return flags as Record<Treatments, boolean>
}

const commonTreatments: Treatments[] = Object.values(Treatments)

export type CommonTreatments = Record<Treatments, boolean>

export const loadCommonTreatments = (
  workspaceId = 'BASIC',
): Promise<CommonTreatments> => {
  return client
    .getTreatments(workspaceId, commonTreatments)
    .then(formatTreatments)
}
